import React from 'react';
import Interactive, { Interaction } from '../drag-event-interactive';
import { HsvaColor, hsvaToHslaString } from '../utils/color-convert';
import { Pointer, PointerProps } from './Pointer';

export interface SaturationProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
    prefixCls?: string;
    /** hsva => `{ h: 0, s: 75, v: 82, a: 1 }` */
    hsva: HsvaColor;
    radius?: React.CSSProperties['borderRadius'];
    /** React Component, Custom pointer component */
    pointer?: ({ prefixCls, left, top, color }: PointerProps) => JSX.Element;
    onChange?: (newColor: HsvaColor) => void;
    onDragStart?: () => void;
    onDragEnd?: () => void;
}

const Saturation = React.forwardRef<HTMLDivElement, SaturationProps>((props, ref) => {
    const {
        prefixCls = 'c-color-saturation',
        radius = 0,
        pointer,
        className,
        style,
        hsva,
        onChange,
        onDragStart,
        onDragEnd,
        ...other
    } = props;
    const containerStyle: React.CSSProperties = {
        width: 200,
        height: 200,
        borderRadius: radius,
        ...style,
        position: 'relative',
    };

    const handleChange = (interaction: Interaction, event: MouseEvent | TouchEvent) => {
        if (event.type === 'mousedown' || event.type === 'touchstart') {
            onDragStart?.();
        }
        onChange &&
            onChange({
                h: hsva.h,
                s: interaction.left * 100,
                v: (1 - interaction.top) * 100,
                a: hsva.a,
                // source: 'hsv',
            });
    };

    const comProps = {
        top: `${100 - hsva.v}%`,
        left: `${hsva.s}%`,
        color: hsvaToHslaString(hsva),
    };

    return (
        <Interactive
            className={[prefixCls, className || ''].filter(Boolean).join(' ')}
            {...other}
            style={{
                position: 'absolute',
                inset: 0,
                cursor: 'pointer',
                backgroundImage: `linear-gradient(0deg, #000, transparent), linear-gradient(90deg, #fff, hsl(${hsva.h}, 100%, 50%))`,
                ...containerStyle,
            }}
            ref={ref}
            onMove={handleChange}
            onDown={handleChange}
            onUp={onDragEnd}
        >
            {React.createElement(pointer || Pointer, {
                prefixCls,
                ...comProps,
            })}
        </Interactive>
    );
});

Saturation.displayName = 'Saturation';

export default Saturation;
